import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';

export interface LookupItem {
  id: number;
  name: string;
}

export interface AdminRef {
  id: number;
  name?: string;
  email: string;
  picture?: string | null;
}

export interface LogEntry {
  id: number;
  change_datetime: string;
  title: string;
  summary: string;
  commit_link?: string | null;
  is_deleted: boolean;
  environment: LookupItem;
  commit_type: LookupItem;
  module: LookupItem;
  created_by: AdminRef;
  updated_by?: AdminRef | null;
  deleted_by?: AdminRef | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface LogEntryListResponse {
  items: LogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface LogEntryFilters {
  commit_type_id?: number | null;
  search?: string | null;
  date_from?: string | null;
  date_to?: string | null;
  include_deleted?: boolean;
}

export interface CreateLogEntryPayload {
  change_datetime: string;
  title: string;
  summary: string;
  commit_type_id: number;
  commit_link?: string | null;
}

export type UpdateLogEntryPayload = Partial<CreateLogEntryPayload>;

export interface ChangeLogsAdmin {
  email: string;
  name: string;
  picture?: string;
}

@Injectable({ providedIn: 'root' })
export class ChangeLogsApiService {
  private static readonly TOKEN_KEY = 'changeLogs.jwt';
  private static readonly ADMIN_KEY = 'changeLogs.admin';

  constructor(private api: ApiService) {}

  get token(): string | null {
    try { return sessionStorage.getItem(ChangeLogsApiService.TOKEN_KEY); } catch { return null; }
  }

  get isLoggedIn(): boolean {
    return !!this.token;
  }

  get currentAdmin(): ChangeLogsAdmin | null {
    try {
      const raw = sessionStorage.getItem(ChangeLogsApiService.ADMIN_KEY);
      return raw ? JSON.parse(raw) as ChangeLogsAdmin : null;
    } catch { return null; }
  }

  setSession(token: string, admin: ChangeLogsAdmin): void {
    try {
      sessionStorage.setItem(ChangeLogsApiService.TOKEN_KEY, token);
      sessionStorage.setItem(ChangeLogsApiService.ADMIN_KEY, JSON.stringify(admin));
    } catch {}
  }

  clearSession(): void {
    try {
      sessionStorage.removeItem(ChangeLogsApiService.TOKEN_KEY);
      sessionStorage.removeItem(ChangeLogsApiService.ADMIN_KEY);
    } catch {}
  }

  private authHeaders(): Record<string, string> {
    const token = this.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  login(idToken: string): Observable<{ token: string; email: string; name: string; picture?: string; created_at: string; expires_at: string }> {
    return this.api.dispatch('/login', { id_token: idToken });
  }

  getCommitTypes(): Observable<LookupItem[]> {
    return this.api.dispatch<LookupItem[]>('/change-logs/commit-types', {}, this.authHeaders());
  }

  listEntries(filters: LogEntryFilters, page: number, pageSize: number): Observable<LogEntryListResponse> {
    const payload: Record<string, unknown> = {
      page,
      page_size: pageSize,
      include_deleted: !!filters.include_deleted,
    };
    if (filters.commit_type_id) payload['commit_type_id'] = filters.commit_type_id;
    if (filters.search) payload['search'] = filters.search;
    if (filters.date_from) payload['date_from'] = filters.date_from;
    if (filters.date_to) payload['date_to'] = filters.date_to;
    return this.api.dispatch<LogEntryListResponse>('/change-logs/entries/list', payload, this.authHeaders());
  }

  createEntry(payload: CreateLogEntryPayload): Observable<LogEntry> {
    return this.api.dispatch<LogEntry>('/change-logs/entries', payload, this.authHeaders());
  }

  updateEntry(id: number, payload: UpdateLogEntryPayload): Observable<LogEntry> {
    return this.api.dispatch<LogEntry>(`/change-logs/entries/${id}/update`, payload, this.authHeaders());
  }

  deleteEntry(id: number): Observable<{ id: number; is_deleted: boolean; deleted_at: string }> {
    return this.api.dispatch<{ id: number; is_deleted: boolean; deleted_at: string }>(
      `/change-logs/entries/${id}/delete`,
      {},
      this.authHeaders()
    );
  }
}
